import { useState } from "react";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, KeyRound, AlertTriangle } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { status } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (status === "authed") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    if (pw !== confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password berhasil di-reset. Silakan login.");
      nav("/login");
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal reset password";
      toast.error(typeof msg === "string" ? msg : "Gagal reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFBF7]">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="link-back-login-from-reset"
          className="inline-flex items-center gap-2 text-sm text-[#586D61] hover:text-[#082618] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali ke Login
        </Link>

        <div className="bg-white border border-[#EBE6DE] rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-[#D16B4D] flex items-center justify-center">
              <PondokIcon className="h-6 w-6 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-[#172E21]">PondokRaja</div>
              <div className="text-xs text-[#586D61]">Reset Password</div>
            </div>
          </div>

          {!token ? (
            <div data-testid="reset-password-invalid-token" className="flex flex-col items-center text-center gap-3 py-4">
              <div className="h-14 w-14 rounded-full bg-[#D14D4D]/10 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-[#D14D4D]" />
              </div>
              <h2 className="font-heading text-2xl text-[#172E21]">Token tidak valid</h2>
              <p className="text-sm text-[#586D61] leading-relaxed">
                Link reset password tidak lengkap. Silakan minta link baru dari halaman Lupa Password.
              </p>
              <Link
                to="/forgot-password"
                className="text-sm text-[#D16B4D] hover:underline font-medium mt-2"
              >
                Minta link baru
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-5">
              <div>
                <h1 className="font-heading text-3xl font-medium text-[#172E21] flex items-center gap-2">
                  <KeyRound className="h-6 w-6 text-[#D16B4D]" /> Buat Password Baru
                </h1>
                <p className="text-sm text-[#586D61] mt-1.5 leading-relaxed">
                  Masukkan password baru Anda. Minimal 8 karakter.
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Password Baru</Label>
                <Input
                  data-testid="input-reset-new-password"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Minimal 8 karakter"
                  className="mt-1.5 border-[#EBE6DE] font-mono"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Konfirmasi Password Baru</Label>
                <Input
                  data-testid="input-reset-confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ulangi password baru"
                  className="mt-1.5 border-[#EBE6DE] font-mono"
                  required
                  minLength={8}
                />
              </div>

              <Button
                data-testid="btn-reset-submit"
                type="submit"
                disabled={loading}
                className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl py-6 mt-2"
              >
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Memproses…</> : "Reset Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
